// Dual-Space View types
// Problem-Solution Map: cross-links problem space entities to solution space entities

// ============================================================
// ENTITIES
// ============================================================

export interface CustomerNeed {
  id: string
  projectId: string
  accountId: string
  personaSessionId: string
  jtbdId: string
  statement: string
  source: 'manual' | 'ai_synthesised' | 'ai_synthesised_edited'
  contributingPainIds: string[]
  contributingGainIds: string[]
  confidenceScore: number
  createdAt: string
  updatedAt: string
}

export interface SolutionBenefit {
  id: string
  featureId: string
  projectId: string
  accountId: string
  statement: string
  linkedGainIds: string[]
  sortOrder: number
  createdAt: string
  updatedAt: string
}

// ============================================================
// CROSS-LINKS
// ============================================================

export type CrossLinkEntityType = 'pain' | 'gain' | 'need' | 'jtbd' | 'capability' | 'feature' | 'benefit'
export type CrossLinkRelation = 'addresses' | 'enables' | 'serves'
export type CrossLinkProvenance = 'manual' | 'ai_suggested' | 'migrated'

export interface CrossLink {
  id: string
  projectId: string
  accountId: string
  sourceType: CrossLinkEntityType
  sourceId: string
  targetType: CrossLinkEntityType
  targetId: string
  relation: CrossLinkRelation
  confidence?: number
  createdBy: CrossLinkProvenance
  rationale?: string
  createdAt: string
}

/** Valid cross-link pairs: source type → allowed target types */
export const VALID_CROSS_LINKS: Record<CrossLinkEntityType, CrossLinkEntityType[]> = {
  pain: ['capability'],
  gain: ['feature', 'capability'],
  need: ['capability'],
  jtbd: ['capability'],
  capability: ['pain', 'gain', 'need', 'jtbd'],
  feature: ['pain', 'gain', 'need'],
  benefit: ['gain'],
}

/** Default relation type for each source→target combination */
export const DEFAULT_RELATION: Record<string, CrossLinkRelation> = {
  'pain→capability': 'addresses',
  'need→capability': 'addresses',
  'gain→feature': 'enables',
  'gain→capability': 'enables',
  'jtbd→capability': 'serves',
  'capability→pain': 'addresses',
  'capability→gain': 'enables',
  'capability→need': 'addresses',
  'capability→jtbd': 'serves',
  'feature→pain': 'addresses',
  'feature→gain': 'enables',
  'feature→need': 'addresses',
  'benefit→gain': 'enables',
}

// ============================================================
// CONFIDENCE LIFECYCLE
// ============================================================

export type ConfidenceLevel = 'hypothesis' | 'signal' | 'pattern' | 'validated' | 'contested' | 'decided'

export function computeConfidenceLevel(
  evidenceCount: number,
  isContested?: boolean,
  isDecided?: boolean,
): ConfidenceLevel {
  if (isDecided) return 'decided'
  if (isContested) return 'contested'
  if (evidenceCount === 0) return 'hypothesis'
  if (evidenceCount === 1) return 'signal'
  if (evidenceCount >= 3) return 'validated'
  return 'pattern'
}

// ============================================================
// DUAL-SPACE VIEW DATA
// ============================================================

export type SpaceNodeType = CrossLinkEntityType | 'persona' | 'alternative'

export interface SpaceNode {
  id: string
  type: SpaceNodeType
  title: string
  confidenceScore: number
  confidenceLevel: ConfidenceLevel
  crossLinkCount: number
  crossLinkIds: string[]
  parentId: string | null
  children: SpaceNode[]
  sourceRef: {
    table: 'persona_workshop_sessions' | 'solution_capabilities' | 'solution_features' | 'customer_needs' | 'solution_benefits'
    id: string
    jsonPath?: string
  }
}

export interface DualSpaceData {
  problemSpace: SpaceNode[]
  solutionSpace: SpaceNode[]
  crossLinks: CrossLink[]
  stats: {
    totalEntities: number
    totalLinks: number
    unlinkedProblems: number
    unlinkedCapabilities: number
  }
}

export interface ValuePropSlot {
  id: string
  title: string
}

export interface ValuePropChain {
  persona?: ValuePropSlot
  alternative?: ValuePropSlot
  need?: ValuePropSlot
  capability?: ValuePropSlot
  feature?: ValuePropSlot
  benefit?: ValuePropSlot
  isComplete: boolean
}

// ============================================================
// GAP ANALYSIS
// ============================================================

export type GapType = 'unsolved_need' | 'unjustified_capability' | 'orphan_pain' | 'unspecified_feature'

export interface Gap {
  type: GapType
  entityType: CrossLinkEntityType
  entityId: string
  entityTitle: string
  message: string
}
